/**
 * API клієнти для invitations + memberships (Порція 3.0.5.2+).
 *
 * Типи синхронізовано з backend (див. backend/src/app/schemas/invitation.py
 * та схему /openapi.json → src/api/schema.gen.ts).
 */
import { api } from './client';

export type TenantRole = 'OWNER' | 'ADMIN' | 'MEMBER';

/** Публічна інформація про запрошення для accept-сторінки (GET без auth). */
export interface InvitationInfo {
  tenant_id: string;
  tenant_name: string;
  invited_email: string;
  tenant_role: TenantRole;
  role_display: string;
  user_exists: boolean;
}

/** Результат POST /invitations/accept. */
export interface InvitationAccepted extends InvitationInfo {
  // Backend повертає те ж InvitationInfo + (для нових user'ів через access)
  // токени можуть бути додані пізніше; зараз accept НЕ повертає TokenPair
  // (новий user мусить логінитися окремо).
}

/** Pending invitation у списку (з admin-сторінки «Команда»). */
export interface InvitationListItem {
  id: string;
  email: string;
  tenant_role: TenantRole;
  invited_at: string;
  invited_by: string;
}

/** Результат POST /invitations (admin створив запрошення). */
export interface InvitationCreated {
  id: string;
  email: string;
  tenant_role: TenantRole;
  invited_at: string;
  invited_by: string;
  user_id: string;
  user_exists: boolean;
  invitation_url: string | null;
}

/**
 * GET /invitations/info?token=… — публічний, без auth.
 * Повертає preview-інфо для accept-сторінки: куди запрошено, яка роль,
 * чи user уже існує (визначає сценарій B vs C).
 */
export async function getInvitationInfo(token: string): Promise<InvitationInfo> {
  const { data } = await api.get<InvitationInfo>('/invitations/info', {
    params: { token },
  });
  return data;
}

/**
 * POST /invitations/accept.
 *
 * Якщо новий user (user_exists=false) — обов'язково передати password
 * (мін. 8 символів). Якщо існуючий — password ігнорується (треба бути
 * залогіненим).
 *
 * Endpoint САМ ігнорує X-Tenant-Id (приймає token замість).
 */
export async function acceptInvitation(payload: {
  token: string;
  password?: string;
}): Promise<InvitationAccepted> {
  const { data } = await api.post<InvitationAccepted>(
    '/invitations/accept',
    payload,
  );
  return data;
}

// ============== Admin endpoints (для 3.0.5.3-4) ============================

/** Запис user_access, що буде створено при accept (Порція 3.0.5.4). */
export interface InitialUserAccess {
  organization_id: string;
  access_role_id: string;
}

/** POST /invitations — створити запрошення (OWNER/ADMIN). */
export async function createInvitation(payload: {
  email: string;
  tenant_role: TenantRole;
  initial_user_access?: InitialUserAccess[];
}): Promise<InvitationCreated> {
  const { data } = await api.post<InvitationCreated>('/invitations', payload);
  return data;
}

/** GET /invitations — список pending (status=INVITED). */
export async function listInvitations(): Promise<InvitationListItem[]> {
  const { data } = await api.get<InvitationListItem[]>('/invitations');
  return data;
}

/** DELETE /invitations/{id} — відкликати pending. */
export async function revokeInvitation(id: string): Promise<void> {
  await api.delete(`/invitations/${id}`);
}

// ============== Memberships (для 3.0.5.3) ==================================

export interface MembershipListItem {
  id: string;
  user_id: string;
  email: string;
  full_name: string;
  tenant_role: TenantRole;
  status: 'ACTIVE' | 'SUSPENDED' | 'INVITED';
  invited_at: string | null;
  accepted_at: string | null;
  suspended_at: string | null;
  suspended_by_email: string | null;
}

/** GET /memberships — список ACTIVE+SUSPENDED tenant'а.
 *  Опційно ?status= для фільтрації. */
export async function listMemberships(
  statusFilter?: 'ACTIVE' | 'SUSPENDED' | 'INVITED',
): Promise<MembershipListItem[]> {
  const { data } = await api.get<MembershipListItem[]>('/memberships', {
    params: statusFilter ? { status: statusFilter } : {},
  });
  return data;
}

export async function suspendMembership(id: string): Promise<void> {
  await api.post(`/memberships/${id}/suspend`);
}

export async function resumeMembership(id: string): Promise<void> {
  await api.post(`/memberships/${id}/resume`);
}

export async function deleteMembership(id: string): Promise<void> {
  await api.delete(`/memberships/${id}`);
}
